import { ArrowRight, ArrowUp, FileText, Lock, PenLine, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { ControlCenterCases } from "@/features/cases/components/control-center-cases";
import { TodaysPriorities } from "@/features/dashboard/components/todays-priorities";
import { MyOnboardingCard } from "@/features/dashboard/components/my-onboarding-card";
import { useDashboardStats } from "@/features/dashboard/hooks/use-dashboard-stats";
import { useNewestArrival } from "@/features/dashboard/hooks/use-newest-arrival";
import { useFirmContext } from "@/features/clients/hooks/use-firm-context";
import { PROGRESS_STAGES, STAGE_LABELS } from "@/features/new-arrivals/api";
import { cn } from "@/lib/utils";

// Header actions — each routes to a real firm-level entry flow. "Add client"
// (gated behind onboarding) and "New action" are rendered separately below.
// "Create case" / "Upload docs" were dropped: both require a client to be
// selected first (there's no firm-level case/upload entry point), so they only
// ever led to the clients list — confusing as labelled quick actions.
const quickActions = [{ label: "Request signature", to: "/reports/esignatures" }];

// Period-over-period trend deltas are demo placeholders — the backend has no
// metrics-history endpoint to compute real month-over-month change yet.
const TREND: Record<string, { delta: string; dir: "up" | "flat" }> = {
  "Active Clients": { delta: "12%", dir: "up" },
  "Open Cases": { delta: "0%", dir: "flat" },
  "Pending IRS Notices": { delta: "5%", dir: "up" },
  "Monthly Revenue": { delta: "18%", dir: "up" },
};

/** "$45.2k"-style compact currency from integer cents, for the KPI card. */
function compactUsd(cents: number): string {
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  return `$${dollars.toFixed(0)}`;
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.name?.split(" ")[0] ?? "there";
  // Scope every KPI to the active workspace so the dashboard follows a house
  // user who has switched into a Business Practitioner (defaults to the CAP book).
  const { firm } = useFirmContext();
  const { data, loading, error, refetch } = useDashboardStats(firm);
  const { arrival } = useNewestArrival();

  const attention = data?.clients?.requiringAttention ?? 0;
  const pendingOnboarding = data?.clients?.pendingOnboarding ?? 0;
  const signaturesAwaiting = data?.signaturesAwaiting ?? 0;
  // No firm-wide pending-transcripts endpoint yet — demo placeholder.
  // TODO(api): replace with a real pending-transcripts count once available.
  const pendingTranscripts = 12;
  // `onboarding_completed` is optional on older sessions; only an explicit
  // `false` means setup is unfinished (matches the OnboardingGate rule).
  const onboardingDone = user?.firm?.onboarding_completed !== false;
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const thisMonth = new Date().toLocaleDateString("en-US", { month: "long" });

  // Newest onboarding client (mid-pipeline) for the navy preview card. The
  // happy-path track is PROGRESS_STAGES; REJECTED is a side-state not on it, so
  // indexOf returns -1 and we surface the rejection reason instead of a step.
  const stageIndex = arrival ? PROGRESS_STAGES.indexOf(arrival.onboardingStage) : -1;
  const stageStep = stageIndex >= 0 ? stageIndex + 1 : null;
  const totalStages = PROGRESS_STAGES.length;
  const isRejected = arrival?.onboardingStage === "REJECTED";

  const openCases = data?.clients?.activeCases ?? 0;
  const noticesCount = data?.pendingNotices ?? 0;

  const kpis = [
    {
      label: "Active Clients",
      value: data?.clients ? data.clients.total.toLocaleString() : null,
      note: "vs. last 30 days",
    },
    {
      label: "Open Cases",
      value: data?.clients ? data.clients.activeCases.toLocaleString() : null,
      note: openCases > 0 ? "In progress" : "Nothing in progress",
    },
    {
      label: "Pending IRS Notices",
      value: data?.pendingNotices != null ? data.pendingNotices.toLocaleString() : null,
      note: noticesCount > 0 ? "Awaiting response" : "Inbox is clear",
    },
    {
      label: "Monthly Revenue",
      value: data?.ar ? compactUsd(data.ar.total) : null,
      note: `${thisMonth}, invoiced`,
    },
  ];

  // "Needs attention" queue — icon well + title/subtitle + large count badge.
  const needsAttention = [
    {
      label: "Pending transcripts",
      sub: "Oldest request 3 days ago",
      value: pendingTranscripts,
      to: "/reports",
      icon: FileText,
      wellClass: "bg-warning-soft text-warning",
      countClass: "bg-primary text-white",
    },
    {
      label: "Onboarding in progress",
      sub: pendingOnboarding > 0 ? "Waiting on documents" : "Nothing in intake",
      value: pendingOnboarding,
      to: "/new-arrivals",
      icon: UserPlus,
      wellClass: "bg-accent text-info",
      // Mockup: counts sit on a light-gray pill; a non-zero count gets dark ink
      // for emphasis, zero stays muted. (Only the transcripts count is orange.)
      countClass: pendingOnboarding > 0 ? "bg-muted text-foreground" : "bg-muted text-muted-foreground",
    },
    {
      label: "Signatures awaiting",
      sub: signaturesAwaiting > 0 ? "Out for signature" : "Nothing out for signature",
      value: signaturesAwaiting,
      to: "/reports/esignatures",
      icon: PenLine,
      wellClass: "bg-muted text-muted-foreground",
      countClass: signaturesAwaiting > 0 ? "bg-muted text-foreground" : "bg-muted text-muted-foreground",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <span className="text-muted-foreground text-sm font-medium tracking-[0.02em]">
            {today}
          </span>
          <h1 className="text-foreground font-heading text-4xl font-semibold tracking-[-0.025em]">
            Good morning, {firstName}.
          </h1>
          <p className="text-muted-foreground mt-1 text-[15px]">
            {attention > 0 ? (
              <>
                <strong className="text-foreground font-semibold">{attention}</strong>{" "}
                {attention === 1 ? "item needs" : "items need"} attention
              </>
            ) : (
              <>
                A quiet board today — <strong className="text-foreground font-semibold">
                  {pendingTranscripts} transcripts
                </strong>{" "}
                are waiting
              </>
            )}
            {pendingOnboarding > 0 && (
              <>
                {" "}and{" "}
                <strong className="text-foreground font-semibold">
                  {pendingOnboarding} {pendingOnboarding === 1 ? "client" : "clients"}
                </strong>{" "}
                still onboarding
              </>
            )}
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Add client is locked until the firm finishes getting-started setup. */}
          {onboardingDone ? (
            <Button asChild variant="outline" className="h-[42px] rounded-sm bg-card/70">
              <Link to="/clients/new">Add client</Link>
            </Button>
          ) : (
            // A disabled <button> suppresses pointer events, so the native
            // tooltip can't fire on it — put the title on a wrapper span that
            // still receives hover, so the "why" is actually reachable.
            <span title="Finish getting started to add clients" className="inline-flex">
              <Button variant="outline" disabled className="h-[42px] rounded-sm bg-card/70">
                <Lock className="size-4" /> Add client
              </Button>
            </span>
          )}
          {quickActions.map((action) => (
            <Button
              key={action.label}
              asChild
              variant="outline"
              className="h-[42px] rounded-sm bg-card/70"
            >
              <Link to={action.to}>{action.label}</Link>
            </Button>
          ))}
          <Button asChild variant="cta" className="h-[42px] rounded-sm">
            <Link to="/clients/new">
              New action <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      {error && !loading && (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/10 text-destructive flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2.5 text-sm"
        >
          <span>Couldn't load your practice stats. {error}</span>
          <Button variant="outline" size="sm" onClick={refetch}>
            Retry
          </Button>
        </div>
      )}

      {/* Getting-started banner — shown until the firm finishes onboarding. */}
      {!onboardingDone && (
        <section
          aria-label="Finish getting started"
          className="shadow-card border-primary/25 relative flex flex-wrap items-center gap-5 overflow-hidden rounded-xl border bg-card px-6 py-5"
        >
          <span
            aria-hidden
            className="bg-primary-soft pointer-events-none absolute -top-12 -left-8 size-40 rounded-full opacity-60 blur-2xl"
          />
          <span className="relative flex size-14 shrink-0 items-center justify-center rounded-full bg-[conic-gradient(var(--primary)_0_33%,var(--muted)_33%_100%)]">
            <span className="font-heading text-warning flex size-11 items-center justify-center rounded-full bg-card text-[13px] font-bold">
              2/6
            </span>
          </span>
          <div className="relative flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-heading text-[17px] font-semibold">
              You're a few steps from your first client.
            </span>
            <span className="text-muted-foreground text-[13px]">
              Finish your letterhead, client portal and IRS connection — adding clients unlocks
              when setup is done.
            </span>
          </div>
          <Button asChild variant="cta" className="relative rounded-sm">
            <Link to="/onboarding">
              Continue setup <ArrowRight className="size-4" />
            </Link>
          </Button>
        </section>
      )}

      {/* Pulse band — one edge-to-edge card, dividers between KPIs */}
      <Card className="grid grid-cols-1 gap-0 overflow-hidden p-0 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi, i) => {
          const trend = TREND[kpi.label];
          return (
            <div
              key={kpi.label}
              className={cn(
                "flex flex-col gap-2.5 px-6 py-5",
                // mobile: stacked rows separated by a top border (not the first)
                i > 0 && "border-t sm:border-t-0",
                // lg: single row, vertical dividers between the four cells
                i < kpis.length - 1 && "lg:border-r",
              )}
            >
              <span className="text-muted-foreground text-xs font-semibold tracking-[0.08em] uppercase">
                {kpi.label}
              </span>
              <div className="flex items-baseline gap-3">
                {loading ? (
                  <div className="bg-muted h-10 w-16 animate-pulse rounded-md" />
                ) : (
                  <span className="font-heading text-[2.75rem] leading-none font-semibold tracking-[-0.03em] tabular-nums">
                    {kpi.value ?? "—"}
                  </span>
                )}
                {trend && (
                  <span
                    className={cn(
                      "flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold",
                      trend.dir === "up"
                        ? "bg-success-soft text-success"
                        : "bg-muted text-muted-foreground",
                    )}
                  >
                    {trend.dir === "up" ? (
                      <>
                        <ArrowUp className="size-3" />
                        {trend.delta}
                      </>
                    ) : (
                      "No change"
                    )}
                  </span>
                )}
              </div>
              <span className="text-muted-foreground text-[13px]">{kpi.note}</span>
            </div>
          );
        })}
      </Card>

      {/* Body: wide-left (Today + Control Center) · narrow-right (queues) */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        <div className="flex flex-col gap-6 lg:col-span-8">
          <TodaysPriorities />
          <ControlCenterCases />
        </div>

        <div className="flex flex-col gap-6 lg:col-span-4">
          {/* My clients to onboard (Team Leader / Staff only; hidden when empty) */}
          <MyOnboardingCard />
          {/* Needs attention queue */}
          <Card className="gap-4 p-5">
            <div className="flex items-center justify-between gap-2 px-1">
              <h2 className="font-heading text-xl font-semibold">Needs attention</h2>
              <span className="text-muted-foreground text-xs font-semibold">3 queues</span>
            </div>
            <div className="flex flex-col gap-1">
              {needsAttention.map((row) => (
                <Link
                  key={row.label}
                  to={row.to}
                  className="hover:bg-muted/60 flex items-center gap-3.5 rounded-md p-2.5 transition-colors"
                >
                  <span
                    className={cn(
                      "flex size-10 shrink-0 items-center justify-center rounded-sm",
                      row.wellClass,
                    )}
                  >
                    <row.icon className="size-[18px]" />
                  </span>
                  <span className="flex min-w-0 flex-grow flex-col leading-tight">
                    <span className="font-semibold">{row.label}</span>
                    <span className="text-muted-foreground text-xs">{row.sub}</span>
                  </span>
                  <span
                    className={cn(
                      "font-heading flex h-10 min-w-10 items-center justify-center rounded-sm px-2.5 text-[22px] font-bold tabular-nums",
                      row.countClass,
                    )}
                  >
                    {loading && row.value === 0 ? "—" : row.value}
                  </span>
                </Link>
              ))}
            </div>
          </Card>

          {/* Client onboarding — navy card */}
          <Card className="bg-brand-panel relative gap-4 overflow-hidden border-0 p-6 text-white">
            <span
              aria-hidden
              className="bg-primary/30 pointer-events-none absolute -right-14 -bottom-20 size-56 rounded-full blur-3xl"
            />
            <div className="relative flex items-center justify-between gap-2">
              <h2 className="font-heading text-lg font-semibold">Client onboarding</h2>
              <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold">
                {stageStep
                  ? `Step ${stageStep} of ${totalStages}`
                  : arrival
                    ? STAGE_LABELS[arrival.onboardingStage]
                    : "All caught up"}
              </span>
            </div>
            {arrival ? (
              <>
                <div className="relative flex items-center gap-3">
                  <span className="font-heading flex size-10 shrink-0 items-center justify-center rounded-sm bg-white/10 text-[13px] font-bold">
                    {arrival.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate font-semibold">{arrival.name}</span>
                    <span className="text-brand-panel-muted truncate text-xs">
                      {isRejected && arrival.onboardingRejectionReason
                        ? `Rejected · ${arrival.onboardingRejectionReason}`
                        : STAGE_LABELS[arrival.onboardingStage]}
                    </span>
                  </div>
                </div>
                <div
                  className="relative grid grid-cols-8 gap-1.5"
                  aria-label={`Onboarding stage ${stageStep ?? 0} of ${totalStages}`}
                >
                  {PROGRESS_STAGES.map((stage, i) => (
                    <span
                      key={stage}
                      className={cn(
                        "h-1.5 rounded-full",
                        stageStep != null && i < stageStep ? "bg-primary" : "bg-white/20",
                      )}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-brand-panel-muted relative text-[13px]">
                No clients are mid-onboarding right now.
              </p>
            )}
            <Button
              asChild
              className="text-brand-panel relative h-[42px] rounded-sm bg-white font-semibold hover:bg-white/90"
            >
              <Link to="/new-arrivals">
                {arrival ? "Continue onboarding" : "Go to arrivals"}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
